<script setup>
import { useThreadsContextStore } from '@/stores/threads'
import { useTicketPriority } from '@/stores/ticketPriority'
import { useToast } from 'vue-toastification'
import { useRouter } from 'vue-router'
import ToastTicketPriorityWarn from '@/components/ToastTicketPriorityWarn.vue'
import { GOOGLE_ICON_OPTIONS } from '@/constants/googleIconNames'


const TOOLTIP_ID = `icon-priority-tool-tip-${Math.random().toString(36).slice(2)}`
const BASE_DATA = { _id: null, name: "No priority set", colour: "#6e6e6e55", icon: "star_unfilled" }
const DELAY = 2000
const TOAST_WARNING_CONFIG = {
  component: ToastTicketPriorityWarn,
  listeners: { navigate: () => router.push('/settings/ticket-priorities') }
}

const props = defineProps({
  threadId: {
    type: String,
    required: true
  },
  ticketPriorityId: { 
    type: [String],
    default: null
  },
  size: {
    type: Number,
    default: 18
  },
})

const threadStore = useThreadsContextStore()
const ticketPriorityStore = useTicketPriority()
const toast = useToast()
const router = useRouter()

let timer = null

const isLoading = ref(false)
const index = ref(-1)
const shouldClear = ref(!!props.ticketPriorityId)

const ticketPriorities = computed(() => ticketPriorityStore.allArray)
const data = computed(() => ticketPriorities.value[index.value] ?? BASE_DATA)

const initializeIndex = () => {
  index.value = ticketPriorities.value.findIndex(tp => tp._id === props.ticketPriorityId)
  shouldClear.value = index.value !== -1
}

const submit = async () => {
  isLoading.value = true
  try {
    await threadStore.patchTicketPriority(props.threadId, data.value._id)
    shouldClear.value = !!data.value._id
  } finally {
    isLoading.value = false
  }
}

const cycleIndex = () => {
  if(!ticketPriorities.value.length) {
    toast.warning(TOAST_WARNING_CONFIG)
    return
  }
  if(isLoading.value) return
  if(shouldClear.value) {
    index.value = -1
    shouldClear.value = false
  } else if (index.value === ticketPriorities.value.length - 1) {
    index.value = -1
  } else {
    index.value += 1
  }

  clearTimeout(timer)
  timer = setTimeout(submit, DELAY)
}

onUnmounted(() => {
  if(timer) {
    clearTimeout(timer)
    submit()
  }
})

watch(() => props.ticketPriorityId, initializeIndex, { immediate: true })
</script>


<template>
  <div
    :id="TOOLTIP_ID"
    class="priority-star"
    @click.stop="cycleIndex"
    :class="{ 'disabled': isLoading }"
  >
    <GoogleIcon
      :name="GOOGLE_ICON_OPTIONS[data.icon]?.icon ?? data.icon"
      :filled="GOOGLE_ICON_OPTIONS[data.icon]?.filled ?? false"
      :colour="data.colour"
      :weight="350"
      size="20px"
    />
  </div>
  <BTooltip :target="TOOLTIP_ID" triggers="hover">{{ data.name }}</BTooltip>
</template>

<style scoped lang="scss">
.priority-star {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  border-radius: 50%;
  padding: 4px;
  cursor: pointer;
  transition: border-color 0.15s ease;
  border: 1.5px solid transparent;
  user-select: none;

  &.disabled {
    cursor: default;
  }

  &:not(.disabled):hover {
    border-color: #8c8c8c55;
  }
}
</style>
